import type { BelongOneLiveChatApi, OneLiveChatStatus } from '#src/types.js';

export const BELONG_ONE_URL = process.env.NEXT_PUBLIC_BELONG_ONE_URL;

export const ONE_LIVE_CHAT_PROOF_PATH = '/api/support/one-live-chat-proof';

const SCRIPT_ID = 'belong-one-live-chat-sdk';
const CONTAINER_ID = 'belong-one-live-chat';
const PLACEHOLDER_LAUNCHER_ID = 'belong-one-live-chat-placeholder';
const LAUNCHER_LABEL = 'Chat with Belong';
const UNAVAILABLE_LABEL = 'Chat unavailable';

let status: OneLiveChatStatus = 'idle';
const listeners = new Set<() => void>();
let scriptLoad: Promise<BelongOneLiveChatApi> | undefined;
let generation = 0;
let isLauncherVisible = false;

function setStatus(next: OneLiveChatStatus) {
  if (status === next) {
    return;
  }

  status = next;
  listeners.forEach((listener) => listener());
}

export function getOneLiveChatStatus(): OneLiveChatStatus {
  return status;
}

export function subscribeOneLiveChatStatus(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function loadScript(url: string): Promise<BelongOneLiveChatApi> {
  scriptLoad ??= new Promise<BelongOneLiveChatApi>((resolve, reject) => {
    const script = document.createElement('script');

    script.id = SCRIPT_ID;
    script.type = 'module';
    script.src = `${url}/one-live-chat-widget.js`;
    script.addEventListener(
      'load',
      () =>
        window.BelongOneLiveChat
          ? resolve(window.BelongOneLiveChat)
          : reject(new Error('Belong One live chat did not register.')),
      { once: true },
    );
    script.addEventListener(
      'error',
      () => reject(new Error('Belong One live chat failed to load.')),
      { once: true },
    );
    document.head.append(script);
  }).catch((error: unknown) => {
    scriptLoad = undefined;
    document.getElementById(SCRIPT_ID)?.remove();

    throw error;
  });

  return scriptLoad;
}

// The widget's show/hide only toggle its panel, so the launcher is hidden here.
function getContainer(): HTMLElement {
  let container = document.getElementById(CONTAINER_ID);

  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    document.body.append(container);
  }

  container.hidden = !isLauncherVisible;

  return container;
}

// Mirrors the widget's launcher while the chat loads, or in its place when it fails to.
const PLACEHOLDER_LAUNCHER_STYLES = `
  #${PLACEHOLDER_LAUNCHER_ID} { position:fixed; right:24px; bottom:24px; z-index:2147483000; display:flex; align-items:center; gap:9px; border-radius:999px; padding:13px 18px; color:white; background:#0b2341; box-shadow:0 10px 34px rgba(11,35,65,.24); font:700 14px/1.45 Montserrat,Inter,system-ui,sans-serif; pointer-events:none; }
  #${PLACEHOLDER_LAUNCHER_ID} > span:first-child { width:14px; height:14px; border-radius:50%; background:#627086; }
  #${PLACEHOLDER_LAUNCHER_ID}[data-loading] > span:first-child { border:2px solid #b9f4df; border-top-color:transparent; background:none; }
  @media (max-width:520px) { #${PLACEHOLDER_LAUNCHER_ID} { right:16px; bottom:16px; } }
`;

function showPlaceholderLauncher(isLoading: boolean) {
  removePlaceholderLauncher();

  const launcher = document.createElement('div');
  const icon = document.createElement('span');
  const label = document.createElement('span');
  const styles = document.createElement('style');

  launcher.id = PLACEHOLDER_LAUNCHER_ID;
  label.textContent = isLoading ? LAUNCHER_LABEL : UNAVAILABLE_LABEL;
  styles.textContent = PLACEHOLDER_LAUNCHER_STYLES;
  launcher.append(icon, label, styles);
  getContainer().append(launcher);

  if (!isLoading) {
    launcher.setAttribute('role', 'status');

    return;
  }

  launcher.dataset.loading = '';
  launcher.setAttribute('aria-hidden', 'true');

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    icon.animate([{ transform: 'rotate(0)' }, { transform: 'rotate(1turn)' }], {
      duration: 800,
      iterations: Infinity,
    });
  }
}

function removePlaceholderLauncher() {
  document.getElementById(PLACEHOLDER_LAUNCHER_ID)?.remove();
}

export async function startOneLiveChat(
  getProof?: () => Promise<string | null>,
): Promise<void> {
  const current = ++generation;

  window.BelongOneLiveChat?.shutdown();

  if (!BELONG_ONE_URL) {
    showPlaceholderLauncher(false);
    setStatus('unavailable');

    return;
  }

  setStatus('loading');
  showPlaceholderLauncher(true);

  try {
    const api = await loadScript(BELONG_ONE_URL);
    const identityToken = (await getProof?.()) ?? undefined;

    if (current !== generation) {
      return;
    }

    removePlaceholderLauncher();
    api.mount({
      apiBaseUrl: BELONG_ONE_URL,
      container: getContainer(),
      launcherLabel: LAUNCHER_LABEL,
      identityToken,
    });
    setStatus(api.isReady() ? 'ready' : 'unavailable');
  } catch {
    if (current === generation) {
      showPlaceholderLauncher(false);
      setStatus('unavailable');
    }
  }
}

export function shutdownOneLiveChat() {
  generation++;
  window.BelongOneLiveChat?.shutdown();
  removePlaceholderLauncher();
  setStatus('idle');
}

export function setOneLiveChatLauncherVisible(visible: boolean) {
  isLauncherVisible = visible;

  const container = document.getElementById(CONTAINER_ID);

  if (container) {
    container.hidden = !visible;
  }
}

export function openOneLiveChat(initialMessage?: string) {
  const api = window.BelongOneLiveChat;
  const container = document.getElementById(CONTAINER_ID);

  if (status !== 'ready' || !api || !container) {
    return;
  }

  container.hidden = false;

  if (initialMessage) {
    api.showNewMessage(initialMessage);
  } else {
    api.show();
  }
}

export async function fetchOneLiveChatProof(): Promise<string> {
  const response = await fetch(ONE_LIVE_CHAT_PROOF_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Chat identity is unavailable.');
  }

  const { proof } = (await response.json()) as { proof?: unknown };

  if (typeof proof !== 'string' || !proof) {
    throw new Error('Chat identity is unavailable.');
  }

  return proof;
}
